import { useCallback, useEffect, useState } from 'react';

/**
 * Which version of a block is in force, per SCOPE, shared across every instance of that block.
 *
 * ## Two pointers, not one
 *
 * Testing a bank and authoring it are different acts, and tying them together made both worse.
 * The test panel simulates a student's draw: stepping it asks "what does version 3 look like to
 * whoever draws it". The editor's heading asks "which version am I writing". Pointing both at one
 * value meant stepping the test panel silently re-seeded every editor field from a version the
 * teacher had not chosen to open — and, because the editor's own fields then fed the live test
 * surface back, left the panel reporting one version while showing another.
 *
 * So `'test'` and `'editor'` are independent. The ONE place they meet is the test panel's Edit
 * button, which deliberately copies test to editor: that is the whole point of the button, and
 * being explicit about it is what makes the rest of the time safe.
 *
 * ## Why this is not component state
 *
 * A selected block is rendered TWICE at once — the card on the canvas and the editor in the side
 * panel are two instances of the same component, not one. Held in `useState`, each kept its own
 * pointer: rerolling the card's test moved the card to version 2 while the editor's heading went
 * on saying "Version 1 of 2", a foot away on the same screen. Two labels disagreeing about one
 * block is worse than having no label at all.
 *
 * That argument is per scope, and it still holds: within a scope there is one answer, and every
 * instance showing that scope must hear it.
 *
 * ## Why not `useBranchJumpTarget`
 *
 * It looks like the same problem and is not. That map holds ONE listener per block
 * (`listeners.set(blockId, setIndex)`), so a second instance replaces the first — which is right
 * for "jump the editor to branch 3", a message with a single recipient, and wrong here, where
 * both instances must hear every change.
 *
 * ## Lifetime
 *
 * Module-level and keyed by block id plus scope, like the branch-jump map, so it survives a block
 * being collapsed and reopened. Nothing prunes it; the bound is blocks visited in one tab, each
 * holding two strings. A pointer left dangling by a deleted version costs nothing either —
 * `pickVersion` falls back to the first version rather than trusting the id.
 */
export type VersionScope = 'test' | 'editor';

const inForce = new Map<string, string | undefined>();
const listeners = new Map<string, Set<(id: string | undefined) => void>>();

const keyFor = (blockId: string, scope: VersionScope) => `${blockId}\u0000${scope}`;

export function useVersionInForce(
  blockId: string,
  scope: VersionScope,
): [string | undefined, (id: string | undefined) => void] {
  const key = keyFor(blockId, scope);
  const [id, setId] = useState(() => inForce.get(key));

  useEffect(() => {
    let subs = listeners.get(key);
    if (!subs) { subs = new Set(); listeners.set(key, subs); }
    subs.add(setId);
    // Adopt whatever the other instance already decided, rather than starting from this one's
    // stale initial read — mount order between the card and the panel is not guaranteed.
    setId(inForce.get(key));
    return () => {
      subs.delete(setId);
      if (subs.size === 0) listeners.delete(key);
    };
  }, [key]);

  const set = useCallback((next: string | undefined) => {
    inForce.set(key, next);
    listeners.get(key)?.forEach(notify => notify(next));
  }, [key]);

  return [id, set];
}
